import React, { useState, useEffect } from "react";
import { Modal, Switch, Typography } from "antd";

import PopulationDisplay from "../../PopulationDisplay";
import { AffiliateLink } from "../../AffiliateLink";
import AlertLink from "../../AlertLink";
import { SaveToFavorites } from "../../favorites/SaveToFavorites";
import AddToCollectionButton from "../../personalCollection/addToCollectionButton";
import { usePopularChartContext } from "../../contexts/popularchartcontext";
import { FAVORITE_TYPES } from "../../../globals";
import { ZERO_REFERENCE_LINE } from "../constants";
import { ENTITY_TYPES } from "../../../utils/constants";
import Helpers from "../../../services/helpers";

const DATA_TYPE_IMPORTANCE = ["max", "min", "avg", "count", "endAverageValue", "percentage", "totalsales"];

export default function PopularChartLegend({
  payload,
  isLiveSearch,
  setAlertModalData,
  hoverFns = { onEnter: () => {}, onLeave: () => {} },
  entityType = ENTITY_TYPES.CARD,
}) {
  const popularChartContext = usePopularChartContext();
  const [items, setItems] = useState([]);

  const { lineProps, hiddenLines, toggleLineHide } = popularChartContext;

  const [identifier, favoriteType] = {
    [ENTITY_TYPES.CARD]: ["cardId", FAVORITE_TYPES.CARD],
    [ENTITY_TYPES.SEALED_WAX]: ["sealedWaxId", FAVORITE_TYPES.SEALED_WAX],
  }[entityType];

  useEffect(() => {
    const itemsAdded = {};
    const newItems = [];

    payload.forEach((entry) => {
      // Account for the reference 0 line
      if (entry.dataKey !== ZERO_REFERENCE_LINE) {
        const entityId = entry.dataKey.split("_")[0];
        const allEntriesInactives =
          entry.inactive &&
          payload.filter((item) => item.dataKey.split("_")[0] === entityId).every((item) => item.inactive);

        if (!itemsAdded[entityId] && (!entry.inactive || allEntriesInactives)) {
          newItems.push(entry);
          itemsAdded[entityId] = true;
          entry[identifier] = entityId;
        }
      }
    });

    if (entityType === ENTITY_TYPES.CARD) {
      newItems.sort(Helpers.sortCardsByNameThenGrade);
    }
    setItems(newItems);
  }, [
    payload,
    // selected,
    lineProps,
  ]);

  const entityImageModal = (image) => {
    Modal.info({
      content: (
        <div>
          <img width={"100%"} src={image} />
        </div>
      ),
      icon: null,
      maskClosable: true,
      onOk() {},
    });
  };

  const itemDataTypes = {};
  payload.forEach((entry) => {
    if (entry.dataKey && !entry.inactive) {
      const [entityId, dataType] = entry.dataKey.split("_");
      if (
        DATA_TYPE_IMPORTANCE.indexOf(dataType) >
        DATA_TYPE_IMPORTANCE.indexOf(itemDataTypes[entityId] && itemDataTypes[entityId].dataType)
      ) {
        itemDataTypes[entityId] = { dataType, color: entry.color };
      }
    }
  });

  return (
    <ul className="recharts-default-legend">
      {items.map((entry) => {
        const color = (itemDataTypes[entry[identifier]] && itemDataTypes[entry[identifier]].color) || "#999";

        return (
          <li
            onMouseOver={hoverFns.onEnter}
            onMouseLeave={hoverFns.onLeave}
            data-key={entry.dataKey}
            key={`item-${entry.dataKey}`}
            style={{ listStyle: "none", position: "relative" }}
            className={`recharts-legend-item ${hiddenLines[entry[identifier]] ? "recharts-legend-item-disabled" : ""}`}
          >
            <div style={{ display: "flex", alignSelf: "stretch" }}>
              <a
                onClick={() => {
                  entry.payload.image ? entityImageModal(entry.payload.image) : null;
                }}
                className={`legend-image ${isLiveSearch ? "not-card" : "card"}`}
                style={{ backgroundColor: color, alignSelf: "stretch" }}
              >
                {!isLiveSearch && (
                  <img
                    width="100%"
                    src={
                      entry.payload.smallImage
                        ? entry.payload.smallImage
                        : entry.payload.image
                        ? entry.payload.image
                        : "/no-image.png"
                    }
                  />
                )}
              </a>
            </div>
            <div className="legend-body-wrapper">
              <div className="card-info">
                <div
                  title={entry.payload.nameWOGrade}
                  className={`legend-text${entry.payload.isRookieCard ? " -rc" : ""}`}
                >
                  {entry.payload.nameWOGrade}
                </div>

                {entry.payload.isRookieCard ? <div className="rookie-card-token">RC</div> : null}

                <div className="legend-body--middle-row">
                  <Typography.Text style={{ color }} className="grade">
                    {entry.payload.grade}&nbsp;
                    {entityType === ENTITY_TYPES.CARD && (
                      <PopulationDisplay
                        id={entry[identifier]}
                        style={{ fontSize: "12px", color: "#444", fontWeight: 400 }}
                        pop={entry.payload.popCount}
                        pop_date={entry.payload.popCountLastUpdated}
                        serialNumber={entry.payload.serialNumber}
                      />
                    )}
                  </Typography.Text>

                  <Switch
                    size="small"
                    style={{ margin: "8px 0" }}
                    checked={!hiddenLines[entry[identifier]]}
                    onClick={() => toggleLineHide(entry[identifier])}
                  />
                </div>
              </div>
              <div className="action-button-container">
                <AffiliateLink
                  cardId={entityType === ENTITY_TYPES.CARD ? entry[identifier] : undefined}
                  sealedWaxId={entityType === ENTITY_TYPES.SEALED_WAX ? entry[identifier] : undefined}
                  entityType={entityType}
                  query={entry.payload.query}
                  className="card-action-icon"
                  tooltipPlacement="bottom"
                />
                <SaveToFavorites
                  className="card-action-icon"
                  foreignId={entry[identifier]}
                  tooltipPlacement="bottom"
                  type={favoriteType}
                />
                <AddToCollectionButton
                  entityType={entityType}
                  className="card-action-icon"
                  cardId={entry[identifier]}
                  tooltipPlacement="bottom"
                />
                <AlertLink
                  cardId={entry[identifier]}
                  openAlertModal={(entityId) =>
                    setAlertModalData({
                      visible: true,
                      [identifier]: entityId,
                      name: entry.payload.name,
                    })
                  }
                  tooltipPlacement="bottom"
                  hasAlert={entry.payload.hasAlert}
                />
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
